import { ComponentPropsWithRef, ReactNode } from 'react';

import Switch from './Switch.tsx';
import classnames from '../utils/classnames.tsx';

// the hover is a step darker than the surface the row sits on
const HOVER = {
    weak: { row: 'hover:bg-surface-medium', withContent: 'has-[>label:hover]:bg-surface-medium' },
    medium: { row: 'hover:bg-surface-strong', withContent: 'has-[>label:hover]:bg-surface-strong' },
};

interface SwitchRowProps extends ComponentPropsWithRef<'label'> {
    label: ReactNode;
    description?: ReactNode;
    aside?: ReactNode;
    value: boolean;
    onValueChange?: (value: boolean) => void;
    disabled?: boolean;
    surface?: keyof typeof HOVER;
}

const SwitchRow = ({
    label,
    description,
    aside,
    value,
    onValueChange,
    disabled = false,
    surface = 'weak',
    className,
    children,
    ...props
}: SwitchRowProps) => {
    const row = (
        <label
            {...props}
            className={classnames(
                'flex items-center gap-4 rounded-lg px-3 py-2 text-sm cursor-pointer',
                !children && HOVER[surface].row,
                disabled && 'cursor-default hover:bg-surface-transparent',
                !children && className
            )}
        >
            <span className="flex flex-1 min-w-0 flex-col gap-0.5">
                <span className="text-content-strong">{label}</span>
                {description && <span className="text-xs text-content-medium">{description}</span>}
            </span>
            {aside && <span className="flex-none">{aside}</span>}
            <span className="flex-none">
                <Switch value={value} onValueChange={onValueChange} disabled={disabled} />
            </span>
        </label>
    );

    if (!children) return row;

    // the content sits outside the label, so a click on it or next to it never toggles the switch; hovering the row
    // still highlights both
    return (
        <div className={classnames('flex flex-col rounded-lg', !disabled && HOVER[surface].withContent, className)}>
            {row}
            <div className="flex flex-col gap-2 px-3 pb-3">{children}</div>
        </div>
    );
};

export default SwitchRow;
